import React from 'react'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'

export const AdminAssistantSettings: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const assistantMeetingsQuota = hasQuota('room.assistantMeetings.value')
  const assistantMeetingsEnabled = assistantMeetingsQuota && (account?.data.effectiveConfig.assistantMeetings ?? false)
  const isAssistantMeetingsEnterpriseManaged = React.useMemo(() => account?.data.enterpriseConfig.assistantMeetings != null, [account])

  const assistantMeetingsEnabledChanged = async (checked: boolean) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, assistantMeetings: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  return (
    <>
      <Container fluid className='p-0'>
        <Row className='mb-3'>
          <Col>
            <h4 className='zocks-text-gray-900'>Admin Assistant</h4>
          </Col>
        </Row>
      </Container>
      <Container className='border border-1 rounded zocks-p-1.25 zocks-mt-1.25'>
        {isAssistantMeetingsEnterpriseManaged && (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <div className='d-flex align-items-center gap-3'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Admin assistant settings are managed at the enterprise level and cannot be modified here.</span>
            </div>
          </Row>
        )}
        <Row className='d-flex align-items-center justify-content-between'>
          <Col className='d-flex align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || !assistantMeetingsQuota || isAssistantMeetingsEnterpriseManaged}
                checked={assistantMeetingsEnabled}
                onChange={(e) => assistantMeetingsEnabledChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
            <span className='ms-2'>Enable Assistants to have meetings on the entire account</span>
          </Col>
        </Row>
        <Container className='mt-4 d-flex flex-column align-items-start zocks-gap-2'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  You can enable Administrative Assistants to have a limited number of meetings. If disabled, Assistants cannot have their own meetings.
                </span>
              </div>
              {!assistantMeetingsQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'room.assistantMeetings.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  )
}
